import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import getAPIbaseURL from "../../utils/helpers"
import { toast } from "react-toastify";
import i18n from "../../i18n";

const API_URL = `${getAPIbaseURL()}/events`;

const formatEvent = (dbEvent) => ({
    id: dbEvent.id,
    title: dbEvent.event_name,
    description: dbEvent.event_description,
    type: dbEvent.category,
    start: dbEvent.start_time,
    end: dbEvent.end_time,
    project: dbEvent.project_id ?? null,
    color: dbEvent.color || '#0A2368',
    allDay: dbEvent.all_day || false,

    attendees: (dbEvent.invitees || []).map(user => ({
        id: user.id,

  
        name:
            user.name ||
            `${user.first_name || ""} ${user.last_name || ""}`.trim(),

        label:
            user.label ||
            `${user.first_name || ""} ${user.last_name || ""}`.trim(),

        avatar:
            user.avatar ||
            user.profile_picture ||
            `https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}&background=random&color=fff`
    }))
});

const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

export const fetchEvents = createAsyncThunk(
    'calendar/fetchEvents',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get(`${API_URL}/`, getAuthHeader());
            return response.data.map(formatEvent);
        } catch (error) {
            toast.error(i18n.t("events.toasts.createError"));
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const createEvent = createAsyncThunk(
    'calendar/createEvent',
    async (eventPayload, thunkAPI) => {
        try {
            const response = await axios.post(`${API_URL}/`, eventPayload, getAuthHeader());
            toast.success(i18n.t("slices.calendarSlice.createSuccess"));
            return formatEvent(response.data); 
        } catch (error) {
            toast.error(i18n.t("slices.calendarSlice.createError"));
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateEvent = createAsyncThunk(
  'calendar/updateEvent',
  async ({ id, payload }, thunkAPI) => {
    try {
      const response = await axios.put(`${API_URL}/${id}`, payload, getAuthHeader());
      toast.success(i18n.t("slices.calendarSlice.updateSuccess"));
      return formatEvent(response.data);
    } catch (error) {
        toast.error(i18n.t("slices.calendarSlice.updateError"));
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'calendar/deleteEvent',
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${API_URL}/${id}`, getAuthHeader());
      toast.success(i18n.t("slices.calendarSlice.deleteSuccess"));
      return id; 
    } catch (error) {
        toast.error(i18n.t("slices.calendarSlice.deleteError"));
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const calendarSlice = createSlice({
    name: "calendar",
    initialState: {
        events: [],
        selectedProject: 'All',
        isLoading: false,
        error: null
    },
    reducers: {
        setProjectFilter: (state, action) => {
            state.selectedProject = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEvents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchEvents.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events = action.payload;
            })
            .addCase(fetchEvents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE EVENT (
            .addCase(createEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events.push(action.payload); 
            })
            .addCase(createEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // PDATE EVENT
            .addCase(updateEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                const index = state.events.findIndex(e => e.id === action.payload.id);
                if (index !== -1) {
                    state.events[index] = action.payload;
                }
            })
            .addCase(updateEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE 
            .addCase(deleteEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events = state.events.filter(e => e.id !== action.payload);
            })
            .addCase(deleteEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { setProjectFilter } = calendarSlice.actions;
export default calendarSlice.reducer;